/**
 * Returns true if this product needs a variation picker before adding to cart.
 * Products with only "Default Title" as their single variant do not need a picker.
 * When variants is null (lazy-loaded), checks hasVariants + options instead.
 */
export function hasRealVariants(product) {
  if (product.hasVariants !== true) return false;
  if (Array.isArray(product.variants) && product.variants.length > 0) return true;
  return Array.isArray(product.options) && product.options.length > 0;
}

/**
 * Fetches full product data including all variants from Shopify Ajax API.
 * Only works when the widget is running on a Shopify store domain.
 * Returns a product object with variants array populated, or null on failure.
 *
 * @param {Object} product - TTPProduct with productPageUrl set
 * @returns {Promise<Object|null>}
 */
export async function fetchFullProduct(product) {
  if (!product.productPageUrl) return null;
  try {
    const handle = product.productPageUrl.split('/products/')[1];
    if (!handle) return null;

    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return null;

    const data = await response.json();

    // Map Shopify Ajax API variant shape to TTPProductVariant shape
    const variants = (data.variants || []).map(v => ({
      id: `gid://shopify/ProductVariant/${v.id}`,
      price: parseFloat(v.price) / 100,
      available: v.available,
      imageUrl: v.featured_image?.src || null,
      option1: v.option1 || null,
      option2: v.option2 || null,
      option3: v.option3 || null,
    }));

    return { ...product, variants };
  } catch (e) {
    console.error('[TTP] Failed to fetch product variants:', e);
    return null;
  }
}

/**
 * Given the full variants array and selected option values in order,
 * returns the matching variant or null if not found.
 * selectedValues = ['Blue', 'M'] → finds variant where option1=Blue, option2=M
 */
export function resolveVariant(variants, selectedValues) {
  return variants.find(v =>
    selectedValues.every((val, i) => v[`option${i + 1}`] === val)
  ) || null;
}

/**
 * For a given option index, returns which values are actually available
 * given the other currently selected options.
 * Used to grey out impossible combos (Blue+XL OOS when Red+XL is fine).
 *
 * @param {Array} variants - full variants array
 * @param {number} optionIndex - which option dimension we are checking
 * @param {Array} currentSelections - current selected value per option index (null = not yet selected)
 */
export function getAvailableValues(variants, optionIndex, currentSelections) {
  const allValues = [...new Set(variants.map(v => v[`option${optionIndex + 1}`]).filter(Boolean))];
  return allValues.filter(val => {
    const testSelections = [...currentSelections];
    testSelections[optionIndex] = val;
    return variants.some(v =>
      testSelections.every((s, i) => !s || v[`option${i + 1}`] === s) &&
      v.available
    );
  });
}

/**
 * Returns the currency symbol for a given currency code.
 */
export function currencySymbol(currency) {
  const map = { USD: '$', ILS: '₪', EUR: '€', GBP: '£' };
  return map[currency] || currency;
}
